export type LicenseDownloadProduct = {
  name?: string | null;
  version?: string | null;
};

export type CatalogHostApp = {
  id: string;
  version?: string;
  downloadPage?: string;
};

export type CatalogProduct = {
  downloadPage?: string;
  isLatest?: boolean;
  supportedHostApplications?: CatalogHostApp[];
};

const versionsMatch = (
  licenseVersion?: string | null,
  hostVersion?: string,
) => {
  if (!licenseVersion || !hostVersion) return false;
  const license = licenseVersion.trim();
  const host = hostVersion.trim();
  return (
    license === host ||
    license.startsWith(`${host}.`) ||
    host.startsWith(`${license}.`)
  );
};

/**
 * Resolve a license to a download URL using catalog host ids
 * (license-database ids), not WordPress slug heuristics.
 */
export const getLicenseDownloadUrlFromCatalog = (
  licenseProduct: LicenseDownloadProduct,
  products: CatalogProduct[] | undefined,
): string | null => {
  const licenseId = licenseProduct.name?.trim();
  if (!licenseId || !products?.length) return null;

  const matches = products.flatMap((product) => {
    const host = product.supportedHostApplications?.find(
      (app) => app.id === licenseId,
    );
    if (!host) return [];
    const url = (host.downloadPage || product.downloadPage || '').trim();
    if (!url) return [];
    return [
      {
        url,
        version: host.version,
        isLatest: Boolean(product.isLatest),
      },
    ];
  });

  if (matches.length === 0) return null;

  const versionMatch = matches.find((match) =>
    versionsMatch(licenseProduct.version, match.version),
  );
  if (versionMatch) return versionMatch.url;

  const latest = matches.find((match) => match.isLatest);
  return (latest ?? matches[0]).url;
};
